import { EmissionsRecordsTab } from "#batteries-included-components/Tabs/Emissions/EmissionsRecordsTab";
import { ChangeLogTab } from "#components/Organization/Facilities/Detail/ChangeLogTab";
import { FacilityOverviewTab } from "#components/Organization/Facilities/Detail/OverviewTab/FacilityOverviewTab";
import { useGetOneFacility } from "#hooks/adapters/useFacilities";
import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useNavigate, useParams, useSearchParams } from "#routers/hooks";
import { ORGANIZATION_BREADCRUMB } from "#routes/organization";
import {
  FACILITIES_LIST_BREADCRUMB,
  FacilitiesListRoute,
} from "#routes/organization/facilities";
import {
  FACILITY_DETAIL_BREADCRUMB,
  linkToFacilityDetail,
} from "#routes/organization/facilities/[facilityId]/index";
import EditFacilityDialog from "#src/batteries-included-components/Dialogs/EditFacilityDialog";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { PageErrorContent } from "#src/batteries-included-components/Layouts/Errors/PageError";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { EntityMeasurementsTab } from "#src/batteries-included-components/Tabs/EntityMeasurementsTab/EntityMeasurementsTab";
import { useUpdateAssetStatusAsync } from "#src/components/hooks/adapters/useAssets";
import { useListEquipment } from "#src/components/hooks/adapters/useEquipment";
import { useListFlows } from "#src/components/hooks/adapters/useFlows";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useListDevices } from "#src/components/hooks/adapters/utils/useDevices";
import { useCreateEditMetaSegments } from "#src/components/hooks/useCreateEditMetaSegments";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { AssociatedFormSubmissionsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedFormSubmissionsTab";
import { getAssetFilters } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflows.helpers";
import { AssociatedWorkflowsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflowsTab";
import { FacilityRecordsTabContent } from "#src/routes/organization/facilities/[facilityId]/FacilityRecordsTabContent";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownMenu,
  generateDocumentTitle,
  Icon,
  Page,
  Pill,
  Tab,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  FacilityAdapter,
  FormSubmissionAdapter,
  isResponseErrorType,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPResponseError,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useState } from "react";
import { Titled } from "react-titled";

const DEFAULT_TAB_KEY = "overview";

export const FacilityDetailPage = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const [searchParams, setSearchParams] = useSearchParams<{
    tab: string;
    form_category_name?: string;
  }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [
    isUpdateFacilityStatusDialogOpen,
    setIsUpdateFacilityStatusDialogOpen,
  ] = useState(false);
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const [areEquipmentEnabled, isLoadingAreEquipmentEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:equipment",
      permissionQuery: {
        $and: ["equipment:read"],
      },
    });
  const [areDevicesEnabled, isLoadingAreDevicesEnabled] = useIsFeatureAvailable(
    {
      featureFlagQuery: "core:devices",
      permissionQuery: {
        $and: ["devices:read"],
      },
    }
  );
  const [areFlowsEnabled, isLoadingAreFlowsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:flows",
    permissionQuery: {
      $and: ["flows:read"],
    },
  });
  const [areFormsEnabled, isLoadingAreFormsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:forms",
    permissionQuery: {
      $and: [
        "form_categories:read",
        "form_schemas:read",
        "form_submissions:read",
      ],
    },
  });
  const [areWorkflowsEnabled, isLoadingAreWorkflowsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:workflows",
      permissionQuery: {
        $and: ["workflows:read", "workflow_templates:read"],
      },
    });
  const [isChangeLogAvailable, isLoadingIsChangeLogAvailable] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:activity_logs",
      permissionQuery: "activities:read",
    });
  const [
    viewConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    ["view-configuration", "table-state", "group-by-tables-state"],
    "assets-shared"
  );
  const [filters] = useStickyState<{ period?: { from?: string } }>(
    {},
    viewConfigStorageKey
  );
  const activePeriod = getYearMonthFromDateRange(filters.period) ?? "";
  const facilityDetailQuery = useGetOneFacility(facilityId, {
    period: activePeriod,
  });
  const createEditMetaSegments = useCreateEditMetaSegments({
    editedAt: facilityDetailQuery.data?.updated_at,
    createdAt: facilityDetailQuery.data?.created_at,
  });
  const facilityDetailBreadcrumbs = useBreadcrumbs(
    [
      ORGANIZATION_BREADCRUMB,
      FACILITIES_LIST_BREADCRUMB,
      FACILITY_DETAIL_BREADCRUMB,
    ],
    {
      2: facilityDetailQuery.data?.name,
    }
  );
  const relatedEquipmentQuery = useListEquipment(
    {
      page: 1,
      pageSize: 1,
      filters: {
        "facility.id": facilityId,
      },
    },
    {
      enabled: areEquipmentEnabled && !!facilityId,
    }
  );
  const relatedDevicesQuery = useListDevices(
    {
      page: 1,
      pageSize: 1,
      filters: {
        "facility.id": facilityId,
      },
    },
    {
      enabled: areDevicesEnabled && !!facilityId,
    }
  );
  const relatedFlowsQuery = useListFlows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        $or: [
          { associated_facility_id: facilityId },
          { downstream_facility_id: facilityId },
          { upstream_facility_id: facilityId },
        ],
        isAlreadyFormatted: true,
      } as any,
    },
    {
      enabled: areFlowsEnabled && !!facilityId,
    }
  );
  const relatedFormSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, Resources.FACILITY, facilityId],
    queryFn: () => {
      return FormSubmissionAdapter.getList({
        filters: {
          related_entity_id: facilityId,
        },
      });
    },
    enabled: areFormsEnabled && !!facilityId,
  });
  const relatedWorkflowsQuery = useListWorkflows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        ...getAssetFilters(Resources.FACILITY, { assetId: facilityId }),
      },
    },
    {
      enabled: areWorkflowsEnabled && !!facilityId,
    }
  );
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();

  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof FacilityAdapter.deleteOne>[0]) => {
      return FacilityAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.FACILITY)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [Resources.FACILITY] });
      navigate(FacilitiesListRoute.toLinkParts());
    },
    onError: (error) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.FACILITY)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.FACILITY)}. An unknown error occurred.`,
        });
      }
    },
  });

  const [updateFacilityStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `update-facility-status-${facilityId}`
  );

  const updateStatusMutation = useUpdateAssetStatusAsync({
    jobKey: updateFacilityStatusJobStorageKey,
    assetId: facilityId,
    setIsUpdating: setIsUpdatingStatus,
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: ["facilities", facilityId, { period: activePeriod }],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
  });

  const handleEditFacilityClick = () => setAssetIdToEdit(facilityId);
  const handleChangeFacilityStatusClick = () =>
    setIsUpdateFacilityStatusDialogOpen(true);

  const handleActiveTabKeyChange = (tabKey: string) => {
    setSearchParams({ tab: tabKey });
  };

  const actionRow = (
    <>
      <IsFeatureAvailable
        key="edit-access-policy"
        featureFlagQuery="core:facilities"
        permissionQuery="facilities:write"
        fallbackChildren={null}
      >
        <Button onClick={handleEditFacilityClick}>Edit</Button>
      </IsFeatureAvailable>
      <IsFeatureAvailable
        key="change-status-access-policy"
        featureFlagQuery="core:facilities"
        permissionQuery="facilities:write"
        fallbackChildren={null}
      >
        <Button
          isLoading={isUpdatingStatus}
          onClick={handleChangeFacilityStatusClick}
        >
          Change Status
        </Button>
      </IsFeatureAvailable>
      <IsFeatureAvailable
        key="more-access-policy"
        featureFlagQuery="core:facilities"
        permissionQuery="facilities:delete"
        fallbackChildren={null}
      >
        <DropdownMenu
          key="more"
          options={[
            {
              label: "Delete",
              variant: "danger",
              isDisabled: deleteMutation.isLoading,
              slotLeft: ({ iconProps }) => (
                <Icon
                  {...iconProps}
                  variant="trash"
                />
              ),
              onClick: () => setIsDeleteConfirmationOpen(true),
            },
          ]}
        >
          <Button
            icon="dots-three-vertical"
            variant="outline"
          />
        </DropdownMenu>
      </IsFeatureAvailable>
    </>
  );

  const hasAssociatedResources =
    !!relatedEquipmentQuery.data?.total_entries ||
    !!relatedDevicesQuery.data?.total_entries ||
    !!relatedFlowsQuery.data?.total_entries ||
    !!relatedFormSubmissionsQuery.data?.total_entries ||
    !!relatedWorkflowsQuery.data?.total_entries;
  const facilityDetail = facilityDetailQuery.data;

  return (
    <>
      <Page
        category={localize("Facility")}
        title={
          facilityDetail ? (
            <>
              {facilityDetail.name}{" "}
              <Pill
                variant={
                  facilityDetail.status === "active" ? "success" : "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(facilityDetail.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            "Detail"
          )
        }
        breadcrumbs={facilityDetailBreadcrumbs}
        isLoading={
          facilityDetailQuery.isLoading ||
          isLoadingLocalization ||
          isLoadingAreFormsEnabled ||
          isLoadingIsChangeLogAvailable ||
          isLoadingAreWorkflowsEnabled ||
          isLoadingAreEquipmentEnabled ||
          isLoadingAreDevicesEnabled ||
          isLoadingAreFlowsEnabled
        }
        actionRow={actionRow}
        onActiveTabKeyChange={handleActiveTabKeyChange}
        activeTabKey={
          !isChangeLogAvailable && searchParams?.tab === "change-log"
            ? DEFAULT_TAB_KEY
            : (searchParams?.tab ?? DEFAULT_TAB_KEY)
        }
        error={facilityDetailQuery.error}
        onErrorRender={({ error }) => <PageErrorContent error={error} />}
        renderMeta={
          createEditMetaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={createEditMetaSegments} />
              )
        }
      >
        <Tab
          tabKey="overview"
          title="Overview"
        >
          <Titled
            title={generateDocumentTitle(
              facilityDetailBreadcrumbs.at(-1)?.title ??
                FACILITY_DETAIL_BREADCRUMB.title,
              localize("facility_plural")
            )}
          >
            <FacilityOverviewTab
              facilityDetail={facilityDetail}
              viewConfigStorageKey={viewConfigStorageKey}
              tableStateStorageKey={tableStateStorageKey}
              groupByTablesStateStorageKey={groupByTablesStateStorageKey}
            />
          </Titled>
        </Tab>
        <Tab
          title={ResourceDefinitions.record.label.plural}
          tabKey="records"
        >
          <Titled
            title={generateDocumentTitle(
              facilityDetail?.name
                ? `${facilityDetail.name} ${ResourceDefinitions.record.label.plural}`
                : `${localize("facility")} ${ResourceDefinitions.record.label.plural}`,
              localize("facility_plural")
            )}
          >
            <FacilityRecordsTabContent facilityId={facilityId} />
          </Titled>
        </Tab>
        <Tab
          tabKey="emissions"
          title={ResourceDefinitions.emissions.label.plural}
        >
          <Titled
            title={generateDocumentTitle(
              facilityDetail?.name
                ? `${facilityDetail.name} ${ResourceDefinitions.emissions.label.plural}`
                : `${localize("facility")} ${ResourceDefinitions.emissions.label.plural}`,
              localize("facility_plural")
            )}
          >
            <EmissionsRecordsTab
              assetType={AssetType.FACILITY}
              assetId={facilityId}
            />
          </Titled>
        </Tab>
        <Tab
          tabKey="measurements"
          title={ResourceDefinitions.measurement.label.plural}
        >
          <Titled
            title={generateDocumentTitle(
              facilityDetail?.name
                ? `${facilityDetail.name} ${ResourceDefinitions.measurement.label.plural}`
                : `${localize("facility")} ${ResourceDefinitions.measurement.label.plural}`,
              localize("facility_plural")
            )}
          >
            <EntityMeasurementsTab
              entityId={facilityId}
              entityType={AssetType.FACILITY}
            />
          </Titled>
        </Tab>
        {areFormsEnabled ? (
          <Tab
            tabKey={Resources.FORM_SUBMISSION}
            title="Forms"
          >
            <Titled
              title={generateDocumentTitle(
                facilityDetail?.name
                  ? `${facilityDetail.name} ${ResourceDefinitions.form_submission.label.plural}`
                  : `${localize("facility")} ${ResourceDefinitions.form_submission.label.plural}`,
                localize("facility_plural")
              )}
            >
              <AssociatedFormSubmissionsTab
                resourceId={facilityId}
                resourceType={Resources.FACILITY}
                defaultFormCategoryName={searchParams.form_category_name}
              />
            </Titled>
          </Tab>
        ) : null}
        {areWorkflowsEnabled ? (
          <Tab
            tabKey={Resources.WORKFLOW}
            title={ResourceDefinitions.workflow.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                facilityDetail?.name
                  ? `${facilityDetail.name} ${ResourceDefinitions.workflow.label.plural}`
                  : `${localize("facility")} ${ResourceDefinitions.workflow.label.plural}`,
                localize("facility_plural")
              )}
            >
              <AssociatedWorkflowsTab
                resourceId={facilityId}
                resourceType={Resources.FACILITY}
              />
            </Titled>
          </Tab>
        ) : null}

        <Tab
          tabKey="change-log"
          title={ResourceDefinitions.activity.label.singular}
          isDisabled={!isChangeLogAvailable}
          iconVariant={!isChangeLogAvailable ? "lock" : undefined}
        >
          <Titled
            title={generateDocumentTitle(
              facilityDetail?.name
                ? `${facilityDetail.name} ${ResourceDefinitions.activity.label.plural}`
                : `${localize("facility")} ${ResourceDefinitions.activity.label.plural}`,
              localize("facility_plural")
            )}
          >
            {isChangeLogAvailable ? (
              <ChangeLogTab resourceId={facilityId} />
            ) : null}
          </Titled>
        </Tab>
      </Page>
      <Dialog
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title={`Delete ${toTitleCaseString(localize(Resources.FACILITY), { intent: "ui" })}`}
        actionRow={[
          <Button
            key="confirm"
            variant="error"
            onClick={() =>
              deleteMutation.mutate({
                id: facilityId,
                previousData: facilityDetail,
              })
            }
            isLoading={deleteMutation.isLoading}
            disabled={hasAssociatedResources}
          >
            Delete
          </Button>,
        ]}
      >
        {hasAssociatedResources ? (
          <>
            You cannot delete {facilityDetail?.name} until the following
            associated resources are deleted:
            <ul style={{ listStyle: "none", padding: 0, marginBlock: 8 }}>
              {relatedEquipmentQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: facilityId,
                      search: `?tab=overview`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedEquipmentQuery.data.total_entries}{" "}
                    {localize(`${Resources.EQUIPMENT}_plural`)}
                  </RoutingLink>
                </li>
              ) : null}
              {relatedDevicesQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToFacilityDetail(facilityId),
                      search: `?tab=overview`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedDevicesQuery.data.total_entries}{" "}
                    {localize(`${Resources.DEVICE}_plural`)}
                  </RoutingLink>
                </li>
              ) : null}
              {relatedFlowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToFacilityDetail(facilityId),
                      search: `?tab=overview`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFlowsQuery.data.total_entries}{" "}
                    {localize(`${Resources.FLOW}_plural`)}
                  </RoutingLink>
                </li>
              ) : null}
              {relatedWorkflowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToFacilityDetail(facilityId),
                      search: `?tab=${Resources.WORKFLOW}`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedWorkflowsQuery.data?.total_entries} Workflows
                  </RoutingLink>
                </li>
              ) : null}
              {relatedFormSubmissionsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={{
                      pathname: linkToFacilityDetail(facilityId),
                      search: `?tab=${Resources.FORM_SUBMISSION}`,
                    }}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFormSubmissionsQuery.data?.total_entries} Form
                    Submissions
                  </RoutingLink>
                </li>
              ) : null}
            </ul>
            Please delete them and return here to delete this{" "}
            {localize(Resources.FACILITY)}.
          </>
        ) : (
          <>
            Are you sure you want to delete this {localize(Resources.FACILITY)}?
            This will also delete its associated Estimation Methods and Records.
            This action cannot be undone.
          </>
        )}
      </Dialog>
      <EditFacilityDialog
        facilityId={assetIdToEdit}
        onClose={() => setAssetIdToEdit(undefined)}
        defaultPeriod={activePeriod}
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateFacilityStatusDialogOpen}
        onClose={() => {
          setIsUpdateFacilityStatusDialogOpen(false);
        }}
        onSubmit={updateStatusMutation.handleUpdateAssetStatusAsync}
        itemCount={1}
        subject={{
          plural: ResourceDefinitions.facility.label.plural,
          singular: ResourceDefinitions.facility.label.singular,
        }}
        warningMessage="This will inactivate all associated equipment, devices, flows, estimation methods. Inactivating estimation methods will delete any associated results for the provided time range."
      />
    </>
  );
};
